/**
 * The only place this app touches web storage. JSON is handled here, and a quota/parse
 * failure is swallowed rather than thrown: persistence is a convenience in every one of
 * these surfaces, never a correctness requirement.
 */
const storage = (): Storage | null => {
  try {
    return window.localStorage
  } catch {
    return null
  }
}

export const storageService = {
  getItem: <T>(key: string): T | null => {
    const raw = storage()?.getItem(key)
    if (!raw) return null
    try {
      return JSON.parse(raw) as T
    } catch {
      return null
    }
  },

  setItem: (key: string, value: unknown): void => {
    try {
      storage()?.setItem(key, JSON.stringify(value))
    } catch {
      /* quota or private mode — the surface works without persistence */
    }
  },

  removeItem: (key: string): void => {
    try {
      storage()?.removeItem(key)
    } catch {
      /* nothing to do */
    }
  },
}

/**
 * Every key this product keeps in web storage, in one place.
 *
 * The names are not cosmetic: a spec written under one of them is picked up by whatever reads it
 * next, and renaming one silently abandons whatever people already have in their browsers. Change a
 * name only with a migration that reads the old one first.
 */
export const StorageKeys = {
  // PLG: written by onboard.html and app.html
  plgSpec: 'intch.connect.spec.v1',
  plgApp: 'intch.app.v1',
  plgTour: 'intch.connect.tour6.v1',
  audience: 'intch.app.audience',
  order: 'intch.app.order',
  introRequest: 'intch.app.introRequest',
  /* The workspace's history of purchased searches, and — until 2026-09-23 — the confirmations per
     search. `picks`/`picksV2` are READ ONCE now, migrated into `crm` (utils/spec/crmBook.ts), and no
     longer written. */
  /** The analytics device id: random, never derived from anything the person typed. */
  device: 'intch.device.v1',
  /** Какая реклама привела человека, первое касание. Живёт до регистрации и до возврата с оплаты —
      см. utils/acquisition.ts, там же правило, почему первое, а не последнее. */
  acquisition: 'intch.acquisition.v1',
  /** The operator's shared token, sent as X-Operator-Token. Typed once per machine; the service is
      what actually checks it. */
  operatorToken: 'intch.operator.token.v1',
  /** order_id внешних оплат, уже зафиксированных на /thank-you: перезагрузка страницы возврата не
      должна ни задвоить запись о покупке, ни второй раз выстрелить Purchase в пиксели. */
  paidOrders: 'intch.app.paidOrders.v1',
  /** Префикс черновиков фильтров окна поиска — по записи поиска (utils/spec/searchDraft.ts). */
  searchDraft: 'intch.app.searchDraft.v1.',
  /** Оплата, начатая со страницы поиска по ссылке «поделиться»: ссылка и спека переживают уход на форму
      оплаты, и /thank-you кладёт купленный поиск в кабинет (utils/spec/sharedCheckout.ts). */
  sharedCheckout: 'intch.app.sharedCheckout.v1',
  /** Последнее назначение воронки, о котором ушло `funnel_assigned` (utils/analytics.ts): событие
      стреляет один раз на смену тройки «воронка × тест × пул», а не на каждую загрузку. */
  funnelAssigned: 'intch.funnel.assigned.v1',
  /** Какие Lead уже ушли в рекламные площадки — по площадке и паре «воронка × тест»
      (utils/marketingConfig.ts): повторная отправка формы не второй лид для оптимизации Меты. */
  adLeads: 'intch.ads.leads.v1',
  picks: 'intch.app.picks',
  picksV2: 'intch.app.picks.v2',
  searches: 'intch.app.searches.v1',
  /* Which PEOPLE this buyer has already been given, so a member who comes back in a second search
     does not cost a second credit. See utils/spec/leadLedger.ts. */
  leadLedger: 'intch.app.leads.v1',
  /** Подтверждённые чатом ответы, списанные из тарифа в ответах (utils/spec/replyLedger.ts). */
  replyLedger: 'intch.app.replies.v1',
  /**
   * Когда этот браузер впервые открыл кабинет в тарифе в ответах. От этого момента пересчитанные пакеты
   * лидов получают свой месяц (utils/spec/replyBalance.ts) — иначе давний пакет сгорел бы задним числом.
   */
  repliesSince: 'intch.app.repliesSince.v1',
  /* The chosen package tier and whether it was cancelled. Local for the same reason the searches
     are: no auth, no payment provider — this is the shape the server record will take. */
  plan: 'intch.app.plan.v1',
  /** Воронка 4: коуч-марк над первым поиском ещё не показан/не снят. Снимается первым запуском. */
  focus: 'intch.app.focus.v1',
  /** Воронка 4: книжка «как это работает» при первом входе в кабинет уже закрыта — когда (ISO).
      Home/onboarding/WelcomeBookStore.ts; второй раз её не показывают. */
  welcomeBook: 'intch.app.welcomeBook.v1',
  /** Подсказку «Start with …» над «Draft messages» закрыли (Home/plan/DraftCoachStore) — больше не показывать. */
  draftCoach: 'intch.app.draftCoach.v1',
  /** Воронка 4: тур по кабинету перед первым шагом онбординга (меню, затем поле ссылки) уже пройден
      или пропущен — когда (ISO). Home/onboarding/PlatformTourStore.ts; второй раз его не показывают. */
  platformTour: 'intch.app.platformTour.v1',
  /** Оценка списка пальцем: ответ (или «закрыл») по id поиска — второй раз про тот же поиск не спрашиваем
      (Home/sheet/rating, 2026-10-08). */
  listRating: 'intch.app.listRating.v1',
  /**
   * КОМПАНИИ ПОКУПАТЕЛЯ — 2026-09-22, «My company» в сайдбаре кабинета. Спека визарда знает один
   * продукт (тот, под который подбирались фильтры); здесь лежат ВСЕ продукты, которые человек
   * заводил, каждый со своей спекой. Активная компания = спека визарда; смена компании переписывает
   * спеку теми же правилами, что смена продукта в онбординге (см. utils/spec/companies.ts).
   */
  companies: 'intch.app.companies.v1',
  /**
   * КАМПАНИИ — 2026-09-22. Цепочки на всех людей одного поиска, собранные разом, и очередь их
   * отправки. Локально по той же причине, что и поиски: авторизации нет, и серверу нечем сказать,
   * чья это кампания. Сами письма и планы лежат на сервере, на строках прогона; здесь — только
   * решение человека («запустил», «одному») и режим. См. utils/spec/campaigns.ts.
   */
  campaigns: 'intch.app.campaigns.v1',
  /* Когда мастер последний раз записал своё состояние. Рядом, а не внутри: форму WizardState читают
     десяток мест, и при входе нужно одно — сравнить, чья запись свежее, браузера или сервера. */
  plgAppAt: 'intch.app.v1.at',
  /* Чей кабинет лежит в этом браузере — номер пользователя Intch. Без него данные первого
     вошедшего уехали бы в кабинет второго, вошедшего с того же компьютера. См. accountSync.ts. */
  accountOwner: 'intch.account.owner.v1',
  /* Когда список компаний и список кампаний последний раз записаны — для того же сравнения при входе. */
  companiesAt: 'intch.app.companies.v1.at',
  campaignsAt: 'intch.app.campaigns.v1.at',
  /**
   * CRM — 2026-09-23. Только то, что РЕШИЛ человек: ручная стадия, сделка, следующий шаг, заметка,
   * «убрать из CRM» — по одной записи на человека, и только у тех, с кем он что-то сделал. Кто в CRM,
   * решают поиски (сервер, POST /api/crm/contacts), а не этот ключ: имена сюда не собираются. См.
   * utils/spec/crmBook.ts. Старые `picks`/`picksV2` переносятся сюда один раз и остаются как были —
   * откат на прошлую версию их прочтёт.
   */
  crm: 'intch.app.crm.v1',
  /* The Pro console keeps its own spec on purpose: the operator tool and the buyer flow are
     separate sessions over one engine, and one spec shared between them would mean an operator's
     experiment silently rewriting a customer's search. */
  consoleSpec: 'intch.salesnav.spec.v1',
  /**
   * ЛОКАЛЬНАЯ «ОПЛАТА ПОНАРОШКУ» (utils/devUnlock.ts). Читается ТОЛЬКО на loopback-хосте: на проде ключ
   * можно выставить руками, и он ничего не сделает — гейт по имени хоста, а не по этому значению.
   */
  devPayments: 'intch.dev.simulatePayments.v1',
  /** Запись тарифа до первой локальной покупки — «Вернуть бесплатное» кладёт её обратно (utils/devUnlock.ts). */
  devPlanBefore: 'intch.dev.planBeforeUnlock.v1',
} as const

// sessionStorage, not localStorage: the onboarding→app handoff must not survive the tab.
export const SessionKeys = {
  onboardHandoff: 'intch.onboard.handoff',
  /**
   * ПРОЧИТАННАЯ СТРАНИЦА И ПАНЕЛЬ, НА КОТОРОЙ ЧЕЛОВЕК СТОИТ — 2026-09-21.
   *
   * До этого дня и то и другое жило только в памяти стора, и цена перезагрузки была «вставь ссылку
   * заново»: одно окно в два экрана (подтверждение → контакт). Воронка 2 растянула это окно до пяти —
   * между чтением и контактом встали три вопроса, — и цена выросла до второго ПЛАТНОГО вызова модели
   * (кэш `page_reads` держит текст страницы, но не вердикт: analyze зовёт модель каждый раз), а
   * человек, потерявший чтение, упирался в отказ `lostRead`, из которого нет выхода вперёд.
   *
   * sessionStorage, а не localStorage: чтение принадлежит ЭТОМУ проходу онбординга, и переживать
   * закрытие вкладки ему нечего — спека к тому моменту уже записана подтверждением.
   */
  onboardProgress: 'intch.onboard.progress',
  /** Which landing button led to /onboard, so the arrival event can name it. See
      utils/onboardingEntry.ts — the landing records the door, the screen reports the entry. */
  onboardDoor: 'intch.onboard.door',
  /** That the entry has already been reported in this tab. A reload of /onboard is not a second
      start; a new tab tomorrow is. */
  onboardStarted: 'intch.onboard.started',
  /**
   * Вкладка уже потратила свою одну перезагрузку из-за пропавшего чанка (utils/lazyRoute.ts), и с тех
   * пор ни один ленивый маршрут не загрузился. Замок от петли: пропажа при стоящей метке — это не
   * старая вкладка после сборки, а поломка, и её показывает экран сбоя. Снимается первой здоровой
   * загрузкой маршрута, поэтому следующий деплой в той же сессии снова получает свою перезагрузку.
   */
  chunkReload: 'intch.chunk.reload',
  /** Перезагрузка из-за пропавшего чанка случилась — событие отправится уже с новой страницы. */
  chunkReloaded: 'intch.chunk.reloaded',
  /**
   * Поля ЗАКАЗА из адреса возврата с оплаты (utils/thankYouParams.ts) — без почты и user_id шлюза.
   * Адрес /thank-you очищается до рендера, и без этой записи перезагрузка в те две секунды, что
   * страница держит подтверждение, показала бы «оплата не прошла» человеку, который только что
   * заплатил. Снимается, когда страница уводит в кабинет.
   */
  thankYouReturn: 'intch.thankYou.return',
  /**
   * Письмо с кодом входа ушло из этой вкладки (components/SignIn/codeResume.ts), с суффиксом входа:
   * `.onboard`, `.signup`, `.login`. Перезагрузка на шаге кода возвращает на шаг кода, а не на пустую
   * форму (2026-10-22). Ни кода, ни токенов — адрес и время письма.
   */
  signInCode: 'intch.signin.code',
  /** Регистрация воронки 4, пережившая перезагрузку: почта, галочки, ушедший Lead (Onboard/contactResume.ts). */
  signUpProgress: 'intch.signup.progress',
} as const

export const sessionService = {
  getItem: (key: string): string | null => {
    try {
      return window.sessionStorage.getItem(key)
    } catch {
      return null
    }
  },
  setItem: (key: string, value: string): void => {
    try {
      window.sessionStorage.setItem(key, value)
    } catch {
      /* private mode */
    }
  },
  removeItem: (key: string): void => {
    try {
      window.sessionStorage.removeItem(key)
    } catch {
      /* private mode */
    }
  },
}
